/**
 * REST API client for Tester Server
 *
 * Uses axios directly (NOT baseRequest.ts) since tester server:
 * - Has no Firebase authentication
 * - Is a local development tool
 * - Has CORS allow-all configured
 */

import axios from "axios";
import type {
  StatusResponse,
  TestResult,
  RunTestRequest,
  TestConfig,
  AnalysisListResponse,
  StoryInfo,
} from "../types";

/**
 * Tester server base URL
 * Configurable via REACT_APP_TESTER_URL environment variable
 * Defaults to localhost:3000 for local development
 */
export const TESTER_BASE_URL =
  process.env.REACT_APP_TESTER_URL || "http://localhost:3000";

/**
 * Version Tester API client
 */
export const versionTesterApi = {
  /**
   * Get current tester server status
   * Returns connected devices, current test state, and last result
   */
  getStatus(): Promise<StatusResponse> {
    return axios.get(`${TESTER_BASE_URL}/api/status`).then((res) => res.data);
  },

  /**
   * Trigger a new test run
   * Returns 202 Accepted with test config
   */
  runTest(
    body: RunTestRequest,
  ): Promise<{ message: string; config: TestConfig }> {
    return axios
      .post(`${TESTER_BASE_URL}/api/tests/run`, body)
      .then((res) => res.data);
  },

  /**
   * Get last completed test result
   * Returns the most recent test result
   */
  getLastResult(): Promise<TestResult> {
    return axios
      .get(`${TESTER_BASE_URL}/api/tests/last`)
      .then((res) => res.data);
  },

  /**
   * List all versions with saved analysis results
   */
  getAnalysisList(): Promise<AnalysisListResponse> {
    return axios.get(`${TESTER_BASE_URL}/api/analyses`).then((res) => res.data);
  },

  /**
   * Get saved analysis result for a specific version
   */
  getAnalysis(version: string): Promise<TestResult> {
    return axios
      .get(`${TESTER_BASE_URL}/api/analyses/${version}`)
      .then((res) => res.data);
  },

  /**
   * Get available stories
   */
  getStories(): Promise<StoryInfo[]> {
    return axios.get(`${TESTER_BASE_URL}/api/stories`).then((res) => res.data);
  },
};
